import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortalRole } from '../../contexts/PortalRoleContext';
import styled from 'styled-components';
import { getMyProfile, updateMyProfile, uploadAvatar } from '../../api/userProfileApi';
import { getTeacherStats } from '../../api/courseGroupViewApi';
import { listSubjectsCached } from '../../api/subjectPortalApi';
import { YEAR_OPTIONS, DAY_OF_WEEK_OPTIONS } from '../../api/courseGroupPortalApi';
import { ATAR_SUBJECTS, teachesAtarYears, keepAtarSubjectsForGrades } from '../../utils/atarSubjects';
import { listCampuses } from '../../api/campusPortalApi';
import AvatarUpload from '../../components/Portal/AvatarUpload';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 80px 24px 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;

  h1 {
    color: #111827;
    font-size: 28px;
    font-weight: bold;
    margin: 0;
  }
`;

const ProfileGrid = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ProfileCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 24px;
  text-align: center;
`;

const ProfileName = styled.h2`
  color: #111827;
  font-size: 24px;
  font-weight: bold;
  margin: 16px 0 8px 0;
`;

const ProfileRole = styled.p`
  color: #6b7280;
  font-size: 14px;
  margin: 0 0 4px 0;
`;

const ProfileBadge = styled.span`
  display: inline-block;
  background: #10b981;
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  margin-top: 12px;
`;

const StatRow = styled.div`
  display: flex;
  justify-content: space-around;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
`;

const StatItem = styled.div`
  text-align: center;

  .value {
    color: #111827;
    font-size: 24px;
    font-weight: bold;
    margin-bottom: 4px;
  }

  .label {
    color: #6b7280;
    font-size: 12px;
    text-transform: uppercase;
  }
`;

const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 20px 24px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;

  h3 {
    color: #111827;
    font-size: 18px;
    font-weight: 600;
    margin: 0;
  }
`;

const CardContent = styled.div`
  padding: 24px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  color: #374151;
  font-size: 14px;
  font-weight: 500;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #F1AF59;
    box-shadow: 0 0 0 3px rgba(241, 175, 89, 0.15);
  }

  &:disabled {
    background: #f9fafb;
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

const Textarea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  transition: all 0.2s;
  grid-column: span 2;

  &:focus {
    outline: none;
    border-color: #F1AF59;
    box-shadow: 0 0 0 3px rgba(241, 175, 89, 0.15);
  }

  @media (max-width: 768px) {
    grid-column: span 1;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
  margin-top: 20px;
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;

  &.primary {
    background: #F1AF59;
    color: white;

    &:hover {
      background: #e09c40;
    }

    &:disabled {
      background: #fcd9a8;
      cursor: not-allowed;
    }
  }

  &.secondary {
    background: white;
    color: #6b7280;
    border-color: #d1d5db;

    &:hover {
      background: #f9fafb;
    }
  }
`;

const Alert = styled.div`
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 20px;
  font-size: 14px;

  &.success {
    background: #f0fdf4;
    color: #047857;
    border: 1px solid #86efac;
  }

  &.warning {
    background: #fef3c7;
    color: #92400e;
    border: 1px solid #fde68a;
  }

  &.error {
    background: #fef2f2;
    color: #dc2626;
    border: 1px solid #fecaca;
  }
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;

  &:not(:last-child) {
    border-bottom: 1px solid #f3f4f6;
  }

  .label {
    color: #6b7280;
    font-size: 14px;
  }

  .value {
    color: #111827;
    font-size: 14px;
    font-weight: 500;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: #6b7280;
  font-size: 16px;
`;

const CheckboxGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const CheckboxOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border: 2px solid ${props => props.$selected ? '#F1AF59' : '#e5e7eb'};
  background: ${props => props.$selected ? '#fef3e2' : '#fff'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;

  &:hover {
    border-color: ${props => props.$selected ? '#F1AF59' : '#d1d5db'};
    background: ${props => props.$selected ? '#fef3e2' : '#f9fafb'};
  }

  span {
    font-size: 14px;
    color: #374151;
    font-weight: ${props => props.$selected ? '500' : '400'};
  }
`;

const SectionLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 10px;
  margin-top: ${props => props.$marginTop ? '20px' : '0'};
`;

/**
 * One line of "I am free on …". Deliberately plain: a teacher fills this in once and then
 * leaves it alone, so it is a row of three controls and a way to remove it, nothing more.
 */
const AvailabilityRow = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr 1fr auto;
  gap: 10px;
  align-items: center;
  margin-bottom: 10px;
`;

const AvailabilitySelect = styled.select`
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #F1AF59;
  }
`;

const RemoveRowButton = styled.button`
  border: none;
  background: #fee2e2;
  color: #b91c1c;
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #fecaca;
  }
`;

const AddRowButton = styled.button`
  border: 1px dashed #d1d5db;
  background: white;
  color: #4b5563;
  border-radius: 8px;
  padding: 9px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    border-color: #F1AF59;
    color: #b45309;
  }
`;

const HelpText = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin: 0 0 12px 0;
  line-height: 1.5;
`;

const AvatarContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
`;

const TeacherProfile = () => {
  const navigate = useNavigate();
  const { currentRole, hasRole } = usePortalRole();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [showMessage, setShowMessage] = useState('');

  // Form states
  const [personalInfo, setPersonalInfo] = useState({
    name: '',
    birthday: '',
    hireDate: '',
    department: '',
    position: '',
    email: '',
    phone: '',
    address: '',
    avatarUrl: ''
  });

  const [professionalInfo, setProfessionalInfo] = useState({
    education: '',
    bio: ''
  });

  // Teaching preferences
  const [teachingPrefs, setTeachingPrefs] = useState({
    teachableGrades: [],
    teachableSubjectIds: []
  });
  const [subjects, setSubjects] = useState([]);

  // The weekly windows this teacher is free in. Offered only at campuses that have turned the
  // feature on, so the rest of the network does not grow a section nobody was asked to fill in.
  const [availability, setAvailability] = useState([]);
  const [availabilityEnabled, setAvailabilityEnabled] = useState(false);

  // Teacher stats (classes and students count)
  const [teacherStats, setTeacherStats] = useState({
    classesCount: 0,
    studentsCount: 0
  });

  // Load profile and teacher stats on mount
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        setError('');
        const data = await getMyProfile();

        // Populate form states
        setPersonalInfo({
          name: data.name || '',
          birthday: data.birthday || '',
          hireDate: data.hireDate || '',
          department: data.department || '',
          position: data.position || '',
          email: data.email || '',
          phone: data.phone || '',
          address: data.address || '',
          avatarUrl: data.avatarUrl || ''
        });

        setProfessionalInfo({
          education: data.education || '',
          bio: data.bio || ''
        });

        setTeachingPrefs({
          teachableGrades: data.teachableGrades || [],
          teachableSubjectIds: data.teachableSubjectIds || []
        });

        setAvailability(data.availability || []);

        // Which campuses this teacher works at decides whether they are asked for their hours
        // at all. Fails soft: not being able to read the campus list is no reason to break the
        // rest of the profile.
        try {
          const campusList = await listCampuses();
          const mine = new Set([
            ...(data.campusIds || []),
            ...(data.teachingCampusIds || []),
            data.campusId
          ].filter(Boolean));
          setAvailabilityEnabled(
            campusList.some(c => mine.has(c.id) && c.teacherAvailability === true)
          );
        } catch (campusErr) {
          console.warn('Could not load campuses:', campusErr);
        }

        // Fetch subjects for teaching preferences
        try {
          const subjectList = await listSubjectsCached();
          setSubjects(subjectList);
        } catch (subErr) {
          console.warn('Could not load subjects:', subErr);
        }

        // Fetch teacher stats
        try {
          const stats = await getTeacherStats();
          setTeacherStats(stats);
        } catch (statsErr) {
          console.warn('Could not load teacher stats:', statsErr);
        }
      } catch (err) {
        console.error('Error loading profile:', err);
        setError('Failed to load profile. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  // Calculate employment duration
  const calculateDuration = (hireDate) => {
    if (!hireDate) return 'N/A';
    const hire = new Date(hireDate);
    const now = new Date();
    const years = now.getFullYear() - hire.getFullYear();
    const months = now.getMonth() - hire.getMonth();

    if (months < 0) {
      return `${years - 1} years, ${12 + months} months`;
    }
    return `${years} years, ${months} months`;
  };

  const handlePersonalSave = async () => {
    try {
      setIsSaving(true);
      setError('');
      await updateMyProfile({
        phone: personalInfo.phone,
        birthday: personalInfo.birthday,
        address: personalInfo.address
      });
      setShowMessage('personal');
      setTimeout(() => setShowMessage(''), 3000);
    } catch (err) {
      console.error('Error saving personal info:', err);
      setError('Failed to save personal information. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleProfessionalSave = async () => {
    try {
      setIsSaving(true);
      setError('');
      await updateMyProfile({
        education: professionalInfo.education,
        bio: professionalInfo.bio
      });
      setShowMessage('professional');
      setTimeout(() => setShowMessage(''), 3000);
    } catch (err) {
      console.error('Error saving professional info:', err);
      setError('Failed to save professional information. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleGrade = (gradeValue) => {
    setTeachingPrefs(prev => {
      const grades = prev.teachableGrades.includes(gradeValue)
        ? prev.teachableGrades.filter(g => g !== gradeValue)
        : [...prev.teachableGrades, gradeValue];
      // Unticking the last of Year 11/12 takes the ATAR subjects with it
      return {
        ...prev,
        teachableGrades: grades,
        teachableSubjectIds: keepAtarSubjectsForGrades(prev.teachableSubjectIds, grades)
      };
    });
  };

  const toggleSubject = (subjectId) => {
    setTeachingPrefs(prev => {
      const subjects = prev.teachableSubjectIds.includes(subjectId)
        ? prev.teachableSubjectIds.filter(s => s !== subjectId)
        : [...prev.teachableSubjectIds, subjectId];
      return { ...prev, teachableSubjectIds: subjects };
    });
  };

  const addAvailability = () => {
    setAvailability(prev => [...prev, { dayOfWeek: 1, startTime: '16:00', endTime: '18:00' }]);
  };

  const updateAvailability = (index, field, value) => {
    setAvailability(prev => prev.map((row, i) => (
      i === index ? { ...row, [field]: field === 'dayOfWeek' ? parseInt(value, 10) : value } : row
    )));
  };

  const removeAvailability = (index) => {
    setAvailability(prev => prev.filter((_, i) => i !== index));
  };

  const handleTeachingPrefsSave = async () => {
    // Checked here as well as on the server, because the server's answer is one message and
    // this can point at the row that is wrong.
    const badRow = availability.findIndex(row => !row.startTime || !row.endTime || row.endTime <= row.startTime);
    if (availabilityEnabled && badRow >= 0) {
      setError(`Availability ${badRow + 1}: the end time has to be after the start time.`);
      return;
    }

    try {
      setIsSaving(true);
      setError('');
      await updateMyProfile({
        teachableGrades: teachingPrefs.teachableGrades,
        teachableSubjectIds: teachingPrefs.teachableSubjectIds,
        ...(availabilityEnabled ? { availability } : {})
      });
      setShowMessage('teaching');
      setTimeout(() => setShowMessage(''), 3000);
    } catch (err) {
      console.error('Error saving teaching preferences:', err);
      setError('Failed to save teaching preferences. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAvatarUpload = (newAvatarUrl) => {
    setPersonalInfo({ ...personalInfo, avatarUrl: newAvatarUrl });
  };

  /**
   * Keep out anyone this page is not for.
   *
   * Checked against every role the user holds, not just their highest one. `currentRole` is the
   * most senior role a person has, so a manager who also teaches resolved to "manager" and was
   * bounced off their own teacher profile — while the navbar went on showing them the link,
   * because that is drawn from the full set. Teaching is the thing this page is about, so
   * holding the teacher role at all is what qualifies.
   */
  useEffect(() => {
    if (!hasRole('teacher') && currentRole !== 'director' && currentRole !== 'system_admin') {
      navigate('/portal');
    }
  }, [hasRole, currentRole, navigate]);

  if (isLoading) {
    return (
      <Container>
        <LoadingContainer>Loading profile...</LoadingContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <h1>Teacher Profile</h1>
      </Header>

      {error && <Alert className="error">{error}</Alert>}

      <ProfileGrid>
        {/* Left Sidebar - Profile Summary */}
        <div>
          <ProfileCard>
            <AvatarContainer>
              <AvatarUpload
                avatarUrl={personalInfo.avatarUrl}
                name={personalInfo.name}
                size={120}
                editable={true}
                onUpload={handleAvatarUpload}
                uploadFunction={uploadAvatar}
              />
            </AvatarContainer>
            <ProfileName>
              {personalInfo.name || 'Unknown'}
            </ProfileName>
            <ProfileRole>{personalInfo.position || 'Teacher'}</ProfileRole>
            <ProfileRole>{personalInfo.department || 'Department'}</ProfileRole>
            <ProfileBadge>Active Teacher</ProfileBadge>

            <StatRow>
              <StatItem>
                <div className="value">{teacherStats.classesCount}</div>
                <div className="label">Classes</div>
              </StatItem>
              <StatItem>
                <div className="value">{teacherStats.studentsCount}</div>
                <div className="label">Students</div>
              </StatItem>
            </StatRow>
          </ProfileCard>

          {/* Quick Info Card */}
          <Card style={{ marginTop: '24px' }}>
            <CardHeader>
              <h3>Quick Information</h3>
            </CardHeader>
            <CardContent>
              <InfoRow>
                <span className="label">Email</span>
                <span className="value">{personalInfo.email || 'N/A'}</span>
              </InfoRow>
              <InfoRow>
                <span className="label">Hire Date</span>
                <span className="value">
                  {personalInfo.hireDate
                    ? new Date(personalInfo.hireDate).toLocaleDateString('en-AU')
                    : 'N/A'}
                </span>
              </InfoRow>
              <InfoRow>
                <span className="label">Employment Duration</span>
                <span className="value">
                  {calculateDuration(personalInfo.hireDate)}
                </span>
              </InfoRow>
              <InfoRow>
                <span className="label">Department</span>
                <span className="value">{personalInfo.department || 'N/A'}</span>
              </InfoRow>
            </CardContent>
          </Card>
        </div>

        {/* Right Side - Detailed Information */}
        <ContentArea>
          {/* Personal Information */}
          <Card>
            <CardHeader>
              <h3>Personal Information</h3>
            </CardHeader>
            <CardContent>
              {showMessage === 'personal' && (
                <Alert className="success">
                  Personal information has been updated successfully!
                </Alert>
              )}

              <FormGrid>
                <FormGroup>
                  <Label>Full Name</Label>
                  <Input
                    value={personalInfo.name}
                    disabled
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={personalInfo.email}
                    disabled
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Date of Birth</Label>
                  <Input
                    type="date"
                    value={personalInfo.birthday}
                    onChange={(e) => setPersonalInfo({ ...personalInfo, birthday: e.target.value })}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Phone</Label>
                  <Input
                    value={personalInfo.phone}
                    onChange={(e) => setPersonalInfo({ ...personalInfo, phone: e.target.value })}
                    placeholder="e.g., 0412 345 678"
                  />
                </FormGroup>

                <FormGroup style={{ gridColumn: '1 / -1' }}>
                  <Label>Address</Label>
                  <Input
                    value={personalInfo.address}
                    onChange={(e) => setPersonalInfo({ ...personalInfo, address: e.target.value })}
                    placeholder="Your address"
                  />
                </FormGroup>
              </FormGrid>

              <ButtonGroup>
                <Button className="secondary" onClick={() => window.location.reload()}>
                  Cancel
                </Button>
                <Button className="primary" onClick={handlePersonalSave} disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
              </ButtonGroup>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <h3>Teaching Preferences</h3>
            </CardHeader>
            <CardContent>
              {showMessage === 'teaching' && (
                <Alert className="success">
                  Teaching preferences have been updated successfully!
                </Alert>
              )}

              <SectionLabel>Teachable Grades</SectionLabel>
              <CheckboxGrid>
                {YEAR_OPTIONS.map(opt => (
                  <CheckboxOption
                    key={opt.value}
                    $selected={teachingPrefs.teachableGrades.includes(opt.value)}
                    onClick={() => toggleGrade(opt.value)}
                  >
                    <input
                      type="checkbox"
                      checked={teachingPrefs.teachableGrades.includes(opt.value)}
                      onChange={() => toggleGrade(opt.value)}
                      style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#F1AF59' }}
                    />
                    <span>{opt.label}</span>
                  </CheckboxOption>
                ))}
              </CheckboxGrid>

              <SectionLabel $marginTop>Teachable Subjects</SectionLabel>
              <CheckboxGrid>
                {[
                  ...subjects.map(s => ({ id: s.id, label: s.subjectName })),
                  ...(teachesAtarYears(teachingPrefs.teachableGrades) ? ATAR_SUBJECTS : [])
                ].map(subject => (
                  <CheckboxOption
                    key={subject.id}
                    $selected={teachingPrefs.teachableSubjectIds.includes(subject.id)}
                    onClick={() => toggleSubject(subject.id)}
                  >
                    <input
                      type="checkbox"
                      checked={teachingPrefs.teachableSubjectIds.includes(subject.id)}
                      onChange={() => toggleSubject(subject.id)}
                      style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#F1AF59' }}
                    />
                    <span>{subject.label}</span>
                  </CheckboxOption>
                ))}
              </CheckboxGrid>

              {/* Only where the campus has switched it on. What a teacher writes here is
                  read when somebody is scheduling a class or looking for relief cover: they
                  see who is free at that hour first. It never blocks a booking — being listed
                  as busy only moves you down the list, it does not take you off it. */}
              {availabilityEnabled && (
                <>
                  <SectionLabel $marginTop>Weekly Availability</SectionLabel>
                  <HelpText>
                    The times you are normally free to teach. Whoever is arranging classes or
                    relief cover sees the teachers who are free at that hour first. Leaving this
                    empty does not stop you being booked — it just means nobody can tell.
                  </HelpText>
                  {availability.map((row, idx) => (
                    <AvailabilityRow key={idx}>
                      <AvailabilitySelect
                        value={row.dayOfWeek}
                        onChange={(e) => updateAvailability(idx, 'dayOfWeek', e.target.value)}
                      >
                        {DAY_OF_WEEK_OPTIONS.map(d => (
                          <option key={d.value} value={d.value}>{d.label}</option>
                        ))}
                      </AvailabilitySelect>
                      <Input
                        type="time"
                        value={row.startTime}
                        onChange={(e) => updateAvailability(idx, 'startTime', e.target.value)}
                      />
                      <Input
                        type="time"
                        value={row.endTime}
                        onChange={(e) => updateAvailability(idx, 'endTime', e.target.value)}
                      />
                      <RemoveRowButton type="button" onClick={() => removeAvailability(idx)}>
                        Remove
                      </RemoveRowButton>
                    </AvailabilityRow>
                  ))}
                  {availability.length === 0 && (
                    <HelpText>No times added yet.</HelpText>
                  )}
                  <AddRowButton type="button" onClick={addAvailability}>
                    + Add a time
                  </AddRowButton>
                </>
              )}

              <ButtonGroup>
                <Button className="secondary" onClick={() => window.location.reload()}>
                  Cancel
                </Button>
                <Button className="primary" onClick={handleTeachingPrefsSave} disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
              </ButtonGroup>
            </CardContent>
          </Card>

          {/* Professional Information */}
          <Card>
            <CardHeader>
              <h3>Professional Information</h3>
            </CardHeader>
            <CardContent>
              {showMessage === 'professional' && (
                <Alert className="success">
                  Professional information has been updated successfully!
                </Alert>
              )}

              <FormGrid>
                <FormGroup style={{ gridColumn: '1 / -1' }}>
                  <Label>Education / Qualifications</Label>
                  <Input
                    value={professionalInfo.education}
                    onChange={(e) => setProfessionalInfo({ ...professionalInfo, education: e.target.value })}
                    placeholder="e.g., Bachelor of Education, Master of Teaching"
                  />
                </FormGroup>

                <Textarea
                  placeholder="Professional Bio - Tell us about yourself, your experience, and teaching style..."
                  value={professionalInfo.bio}
                  onChange={(e) => setProfessionalInfo({ ...professionalInfo, bio: e.target.value })}
                />
              </FormGrid>

              <ButtonGroup>
                <Button className="secondary" onClick={() => window.location.reload()}>
                  Cancel
                </Button>
                <Button className="primary" onClick={handleProfessionalSave} disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
              </ButtonGroup>
            </CardContent>
          </Card>
        </ContentArea>
      </ProfileGrid>
    </Container>
  );
};

export default TeacherProfile;
